import { useState } from 'react';

import { NOTIFICATION_EVENTS as EVENTS } from '@src/constants';
import { useActiveOrg } from '@src/context/hooks';
import { useNotificationPrefsMutation, useNotificationPrefsQuery, useOptionLabels } from '@src/query';

import { Toaster, useToasts } from '../Listings/useToasts';
import { Switch } from './controls';
import PageHeader from './PageHeader';


/** Merge the API list (defaults both true) into a complete `{event: {in_app,
 * email}}` map so every known event has a stable entry to toggle. */
function toPrefMap(list) {
  const byEvent = Object.fromEntries((list || []).map((row) => [row.event, row]));
  return Object.fromEntries(EVENTS.map(({ event }) => {
    const row = byEvent[event] || {};
    return [event, { in_app: row.in_app ?? true, email: row.email ?? true }];
  }));
}


function prefMapToList(map) {
  return EVENTS.map(({ event }) => ({ event, in_app: map[event].in_app, email: map[event].email }));
}


function PrefHeader() {
  return (
    <div className="flex items-center px-1 pb-1">
      <span className="flex-1 text-[10.5px] font-semibold uppercase tracking-[0.4px] text-ink-subtle">Event</span>

      <span className="w-[72px] text-center text-[10.5px] font-semibold uppercase tracking-[0.4px] text-ink-subtle">
        In-app
      </span>

      <span className="w-[72px] text-center text-[10.5px] font-semibold uppercase tracking-[0.4px] text-ink-subtle">
        Email
      </span>
    </div>
  );
}

PrefHeader.displayName = 'PrefHeader';


function PrefRow({ item, label, value, disabled, onToggle }) {
  return (
    <div className="flex items-center rounded-[9px] border border-hairline bg-surface-1 px-3.5 py-3">
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[13.5px] font-medium text-ink">{label}</span>
        <span className="text-[11.5px] text-ink-subtle">{item.description}</span>
      </span>

      <span className="flex w-[72px] justify-center">
        {item.inApp
          ? (
            <Switch
              disabled={disabled}
              checked={value.in_app}
              label={`${value.in_app ? 'Disable' : 'Enable'} in-app ${label}`}
              onChange={() => onToggle('in_app')}
            />
          )
          : <span className="text-[12px] text-ink-tertiary">—</span>}
      </span>

      <span className="flex w-[72px] justify-center">
        <Switch
          disabled={disabled}
          checked={value.email}
          label={`${value.email ? 'Disable' : 'Enable'} email ${label}`}
          onChange={() => onToggle('email')}
        />
      </span>
    </div>
  );
}

PrefRow.displayName = 'PrefRow';


/** Notification preferences (#164, .pen LtPWE): a per-event matrix of in-app +
 * email switches. All roles can manage their own delivery. Toggles save
 * optimistically via PUT (full list upsert); a failed save reverts and toasts.
 * Weekly summary is email-only — its in-app cell is a dash, not a dead toggle. */
function Notifications() {
  const { toasts, push } = useToasts();
  const { seller } = useActiveOrg();
  // Event labels come from the backend (#364); the row order, description copy,
  // and email-only `inApp` flag stay a frontend-only display layer in EVENTS.
  const eventLabels = useOptionLabels('notification_event');

  const prefsQuery = useNotificationPrefsQuery(seller?.id, { enabled: Boolean(seller) });
  const queryKey = prefsQuery.queryKey;

  // Local working copy; only adopted once the server list resolves.
  const [draft, setDraft] = useState(null);

  const saveMutation = useNotificationPrefsMutation();

  if (!seller) {
    return null;
  }

  const serverMap = toPrefMap(prefsQuery.data?.preferences);
  const prefs = draft || serverMap;

  const toggle = (event, channel) => {
    const next = { ...prefs, [event]: { ...prefs[event], [channel]: !prefs[event][channel] } };
    const previous = prefs;
    setDraft(next);
    saveMutation.mutate(
      { sellerId: seller.id, list: prefMapToList(next), queryKey },
      {
        onSuccess: () => setDraft(null),
        onError: () => {
          setDraft(previous);
          push('Could not save preferences', 'danger');
        },
      },
    );
  };

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader title="Notifications" subtitle="Choose what reaches you — in-app, email, or both." />

      <div className="flex w-full flex-col gap-2.5 px-4 pb-6 lg:px-0">
        {prefsQuery.isPending
          ? <span className="py-10 text-center text-[13px] text-ink-subtle">Loading preferences…</span>
          : prefsQuery.isError
            ? <span className="py-10 text-center text-[13px] text-danger">Could not load preferences.</span>
            : (
              <>
                <PrefHeader />

                {EVENTS.map((item) => (
                  <PrefRow
                    item={item}
                    key={item.event}
                    value={prefs[item.event]}
                    disabled={saveMutation.isPending}
                    label={eventLabels[item.event] || item.label}
                    onToggle={(channel) => toggle(item.event, channel)}
                  />
                ))}
              </>
            )}
      </div>

      <Toaster toasts={toasts} />
    </div>
  );
}

Notifications.displayName = 'Notifications';
export default Notifications;
